/**
 * Helper functions para trabajar con fields (dimensions y metrics) de forma unificada
 * Permite trabajar con un array único de IDs mientras se mantiene la diferenciación por tipo
 */

/**
 * Separa un array de field IDs en dimensions, indicators y metrics
 * @param {Array<string>} fields - Array de field IDs
 * @param {Object} fieldsData - Objeto con FIELD_DATA_BY_ID y FIELD_SEGMENTS
 * @returns {{dimensions: Array<string>, indicators: Array<string>, metrics: Array<string>}}
 */
export function separateFieldsByType(fields, fieldsData) {
    if (!fields || !fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_SEGMENTS) {
        return { dimensions: [], indicators: [], metrics: [] };
    }

    const dimensions = [];
    const indicators = [];
    const metrics = [];
    const { FIELD_DATA_BY_ID, FIELD_SEGMENTS } = fieldsData;

    fields.forEach(fieldId => {
        const fieldData = FIELD_DATA_BY_ID[fieldId];
        if (fieldData) {
            if (fieldData.segment === FIELD_SEGMENTS.DIMENSIONS) {
                dimensions.push(fieldId);
            } else if (fieldData.segment === FIELD_SEGMENTS.INDICATORS) {
                indicators.push(fieldId);
            } else if (fieldData.segment === FIELD_SEGMENTS.METRICS) {
                metrics.push(fieldId);
            }
        }
    });

    return { dimensions, indicators, metrics };
}

/**
 * Combina dimensions, indicators y metrics en un solo array manteniendo el orden
 * @param {Array<string>} dimensions - Array de dimension IDs
 * @param {Array<string>} indicators - Array de indicator IDs (opcional)
 * @param {Array<string>} metrics - Array de metric IDs
 * @returns {Array<string>} - Array combinado de field IDs
 */
export function combineFields(dimensions, metrics, indicators = []) {
    return [...(dimensions || []), ...(indicators || []), ...(metrics || [])];
}

/**
 * Obtiene el tipo de un field (DIMENSION, INDICATOR o METRIC)
 * @param {string} fieldId - ID del field
 * @param {Object} fieldsData - Objeto con FIELD_DATA_BY_ID y FIELD_SEGMENTS
 * @returns {string|null} - 'DIMENSION', 'INDICATOR', 'METRIC' o null si no se encuentra
 */
export function getFieldType(fieldId, fieldsData) {
    if (!fieldId || !fieldsData || !fieldsData.FIELD_DATA_BY_ID || !fieldsData.FIELD_SEGMENTS) {
        return null;
    }

    const fieldData = fieldsData.FIELD_DATA_BY_ID[fieldId];
    if (!fieldData) return null;

    const { FIELD_SEGMENTS } = fieldsData;
    if (fieldData.segment === FIELD_SEGMENTS.DIMENSIONS) {
        return 'DIMENSION';
    } else if (fieldData.segment === FIELD_SEGMENTS.INDICATORS) {
        return 'INDICATOR';
    } else if (fieldData.segment === FIELD_SEGMENTS.METRICS) {
        return 'METRIC';
    }

    return null;
}

/**
 * Filtra un array de fields por tipo
 * @param {Array<string>} fields - Array de field IDs
 * @param {string} type - 'DIMENSION' o 'METRIC'
 * @param {Object} fieldsData - Objeto con FIELD_DATA_BY_ID y FIELD_SEGMENTS
 * @returns {Array<string>} - Array filtrado de field IDs
 */
export function filterFieldsByType(fields, type, fieldsData) {
    if (!fields || !type || !fieldsData) {
        return [];
    }

    const { dimensions, metrics } = separateFieldsByType(fields, fieldsData);
    return type === 'DIMENSION' ? dimensions : metrics;
}

